import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { destroySession } from '../../helpers/Session';
import DataTable from 'react-data-table-component';
import LeaveLoader from '../../components/loader/LeaveLoader';
import toast from '../../components/sweetalert/Sweetalert';
import Swal from 'sweetalert2';

const Leaves = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const uname = sessionStorage.getItem('uname') || '';

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        leave: '',
        quantity: '',
        noticeperiod: '',
        status: ''
    });

    const leaveDataKey = ['leaves', currentPage, rowPerPage];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    }

    const resetForm = () => {
        setFormData({
            id: 0,
            leave: '',
            quantity: '',
            noticeperiod: '',
            status: ''
        });
    }

    const tableColumns = [
        { name: 'Leave', selector: row => row.leave },
        { name: 'Quantity', selector: row => row.quantity },
        { name: 'Notice Period (days)', selector: row => row.noticeperiod },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const deleteLeaveMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`leaves/${id}?uname=${uname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(leaveDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.response || 'Something went wrong while deleting leave!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const deleteLeave = (id, event) => {
        Swal.fire({
            text: `Are you sure you want to delete leave details of ${event}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: (result) => {
				return new Promise(function(resolve) {
                    if (result) {
                        deleteLeaveMutation.mutate(id);
                        resolve();
                    }
                });
            }
        });
    };

    const { data: leaves, isLoading: isLoadingLeaves, error: errorLeaves } = useQuery({
        queryKey: leaveDataKey,
        queryFn: async () => {
            const res = await axios.get(`leaves?page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.leaves?.map(item => ({
                leave: item.leave,
                quantity: item.quantity,
                noticeperiod: item.noticeperiod,
                status: item.status === 1 ? <span className="label label-success">Active</span> : <span className="label label-danger">Inactive</span>,
                action: (
                    <div className="buttons">
                        <button type="button" className="btn-edit" onClick={() => setFormData({ id: item.id, leave: item.leave, quantity: item.quantity, noticeperiod: item.noticeperiod, status: item.status })}>
                            <i className="fa fa-edit"></i>
                        </button>
                        <button type="button" className="btn-delete" onClick={() => deleteLeave(item.id, item.leave)}>
                            <i className="fa fa-trash"></i>
                        </button>
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows }
        },
        enabled: !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorLeaves) {
            if (errorLeaves.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error') }, 1000);
            }
        }
    }, [errorLeaves]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    const submitLeaveMutation = useMutation({
        mutationFn: async (leaveData) => {
            const res = await axios.post('leaves', leaveData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            queryClient.invalidateQueries(leaveDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while saving leave details!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitLeave = () => {
        const { leave, quantity, status } = formData;

        if (!leave || !quantity || !status) {
            toast.fire({ icon: 'error', title: "All fields are required." });
            return;
        }

        setFormSubmit(true);
        submitLeaveMutation.mutate({ ...formData, uname });
    };

    return (
        <div className='leave'>
            <div className="page-header">
                <div className="title">
                    <h1>Leaves</h1>
                    <ul className="breadcrumbs">
                        <li>
                            <NavLink to="/settings/devices">
                                Settings
                            </NavLink>
                        </li>
                        <li>
                            <span>Leaves</span>
                        </li>
                    </ul>
                </div>
            </div>


            <div className='panels'>
                <div className='left-panel'>
                    <DataTable 
                        columns={tableColumns} 
                        data={leaves?.data || []}
                        progressPending={isLoadingLeaves} 
                        progressComponent={<LeaveLoader />} 
                        pagination
                        paginationServer
                        paginationTotalRows={leaves?.totalRows || 0}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        dense
                        highlightOnHover
                        responsive
                    />
                </div>

                <div className='right-panel'>
                    <form>
                        <div className='form-group'>
                            <label>Leave</label>
                            <input type="text" name="leave" value={formData.leave} autoComplete='off' onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Quantity</label>
                            <input type='number' name='quantity' value={formData.quantity} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Notice Period (days)</label>
                            <input type='number' name='noticeperiod' value={formData.noticeperiod} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Status</label>
                            <select name='status' value={formData.status} onChange={handleInputChange} disabled={formSubmit}>
                                <option value=''></option>
                                <option value='1'>Active</option>
                                <option value='2'>Inactive</option>
                            </select>
                        </div>
                        <div className='button-group'>
                            <button type='button' onClick={handleSubmitLeave} disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Leaves;